// traceQueryOps.js — what the scan window's agents ASKED of telemetry and
// data systems, and what came back. Two X-ray tabs ride this one lib:
//
//   Grafana — PromQL/LogQL against the Grafana MCP servers (prod-grafana,
//             staging-grafana, …): the query, its range, and the OBSERVED
//             telemetry (series, labels, log hints) — plus the write side,
//             alerting_manage_rules mutations (standing alert rules are
//             governance-grade, like Git writes).
//   Queries — structured analytics_query calls (entity/select/filters graphs)
//             and emissary prompt lookups over MCP, plus SQL driven through
//             the SHELL (clickhouse-client, psql, sqlite3, curl-to-ClickHouse)
//             — the Git-grammar treatment applied to data access.
//
// The RESPONSE is the point: each op captures the result text the agent's
// context actually received (capped), replayable through the same annotated
// renderer as the sibling tabs. Analytics responses are additionally parsed
// ({columns, rows, cost}) into a readable table document plus cost chips —
// a rendering of the observed payload, same license as Linear's comment-list
// formatting.
//
// One op shape, one aggregation, one panel component parameterized per tab:
//   op = { key, channel: mcp|shell, engine, instance, kind: query|write|browse,
//          query, target, fields, content?, result: {text, truncated, len}, ok }
// Targets group ops: a Prometheus METRIC, a Loki stream selector, an analytics
// ENTITY, a SQL table — the noun the window kept interrogating.

import { splitStatements, stripHeredocs, unquoted } from "./traceForgeOps.js";

// Captured response text per op. Responses are the tab's payload, so this is
// generous relative to Linear's prose cap — but a Prometheus result with
// label spam runs long, and a window can hold hundreds of queries.
const RESULT_CAP = 4000;
// Structured tables keep at most this many rows (render caps lower; the CSV
// download gets everything kept here).
const TABLE_ROW_CAP = 200;
// Query text cap (a pathological generated SQL shouldn't balloon scan state).
const QUERY_CAP = 4000;

function str(v) {
  return typeof v === "string" && v ? v : null;
}

function cap(s, max) {
  const t = typeof s === "string" ? s : "";
  return { text: t.length > max ? t.slice(0, max) : t, truncated: t.length > max };
}

// djb2 — stable synthetic dedup key (same as the sibling libs).
function hashString(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

function contentText(content) {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content.map((p) => (typeof p === "string" ? p : p?.text || "")).join("\n");
  }
  return "";
}

function resultErrored(result) {
  if (!result) return false;
  if (result.isError) return true;
  return contentText(result.content).includes("<tool_use_error>");
}

function parseMcpName(name) {
  const s = String(name || "");
  const segs = s.split("__");
  if (segs.length < 3 || segs[0] !== "mcp") return null;
  return { server: segs.slice(1, -1).join("__"), action: segs[segs.length - 1].toLowerCase() };
}

// ── Grafana ──────────────────────────────────────────────────────────────────

// The Grafana instance label: "prod-grafana" → "prod". The server name IS the
// environment fact — which world the agent was watching.
function grafanaInstanceOf(server) {
  return server.replace(/-?grafana-?/i, "") || "grafana";
}

// The first PromQL identifier that isn't a function/keyword — the METRIC the
// expression interrogates, which is the natural grouping noun.
const PROMQL_NON_METRICS = new Set([
  "sum", "rate", "irate", "count", "avg", "max", "min", "by", "without", "on",
  "ignoring", "vector", "increase", "histogram_quantile", "count_over_time",
  "sum_over_time", "avg_over_time", "max_over_time", "min_over_time", "topk",
  "bottomk", "delta", "idelta", "label_replace", "label_join", "or", "and",
  "unless", "offset", "group_left", "group_right", "absent", "clamp_min",
  "clamp_max", "time", "changes", "deriv", "predict_linear", "quantile",
  "stddev", "stdvar", "count_values", "abs", "ceil", "floor", "round", "sort",
  "sort_desc", "scalar", "bool", "group", "last_over_time", "present_over_time",
]);

export function promqlMetricOf(expr) {
  // Labels aren't metrics: strip grouping clauses (by/without/on/…) and
  // matcher braces before scanning, so `sum by (provider) (rate(metric[5m]))`
  // resolves to the metric, not the label.
  const scan = String(expr || "")
    .replace(/\b(by|without|on|ignoring|group_left|group_right)\s*\([^)]*\)/gi, " ")
    .replace(/\{[^}]*\}/g, " ")
    .replace(/\[[^\]]*\]/g, " ");
  for (const m of scan.matchAll(/[a-zA-Z_:][a-zA-Z0-9_:]*/g)) {
    const id = m[0];
    if (id.length < 3 || PROMQL_NON_METRICS.has(id.toLowerCase())) continue;
    return id;
  }
  return null;
}

// A LogQL query's stream selector — `{namespace=~"prod-.*"}` — is its noun.
export function logqlSelectorOf(logql) {
  const m = /\{[^}]*\}/.exec(String(logql || ""));
  return m ? m[0].slice(0, 80) : null;
}

const GRAFANA_BROWSE_RE = /^(list_|search_|get_|find_)/;
const ALERT_WRITE_OPS = new Set(["create", "update", "delete", "pause", "unpause", "resume"]);

// One prompt's turns → its Grafana ops.
export function extractGrafanaOps(turns) {
  const ops = [];
  if (!Array.isArray(turns)) return ops;
  const resultById = new Map();
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type === "tool_result" && b.tool_use_id) resultById.set(b.tool_use_id, b);
    }
  }
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type !== "tool_use") continue;
      const parsed = parseMcpName(b.name);
      if (!parsed || !/grafana/i.test(parsed.server)) continue;
      const input = b.input && typeof b.input === "object" ? b.input : {};
      const result = b.id ? resultById.get(b.id) : null;
      const ok = !resultErrored(result);
      const fullText = ok && result ? contentText(result.content) : "";
      const res = cap(fullText, RESULT_CAP);
      const instance = grafanaInstanceOf(parsed.server);
      const base = {
        key: b.id || `synth:${hashString(parsed.action + JSON.stringify(input))}`,
        channel: "mcp", instance, ok,
        result: { text: res.text || null, truncated: res.truncated },
        view: fullText ? parseGrafanaResult(fullText) : null,
        content: null,
      };

      if (parsed.action === "query_prometheus") {
        const q = cap(str(input.expr) || "", QUERY_CAP);
        const fields = {};
        if (str(input.queryType)) fields.type = input.queryType;
        if (str(input.datasourceUid)) fields.datasource = input.datasourceUid;
        const range = [str(input.startTime), str(input.endTime)].filter(Boolean).join(" → ");
        if (range) fields.range = range;
        if (input.stepSeconds || input.step) fields.step = String(input.stepSeconds || input.step);
        ops.push({ ...base, engine: "prometheus", kind: "query", query: q.text, target: promqlMetricOf(q.text) || "promql", fields });
      } else if (/^query_loki_/.test(parsed.action)) {
        const q = cap(str(input.logql) || "", QUERY_CAP);
        const fields = {};
        if (str(input.queryType)) fields.type = input.queryType;
        if (input.limit) fields.limit = String(input.limit);
        ops.push({ ...base, engine: "loki", kind: "query", query: q.text, target: logqlSelectorOf(q.text) || "logql", fields });
      } else if (parsed.action === "alerting_manage_rules") {
        const operation = (str(input.operation) || "list").toLowerCase();
        const write = ALERT_WRITE_OPS.has(operation);
        const rule = str(input.search_rule_name)
          || str(input.rule?.title) || str(input.title) || str(input.rule_uid) || null;
        const payload = input.rule && typeof input.rule === "object" ? JSON.stringify(input.rule, null, 2) : null;
        const c = payload ? cap(payload, QUERY_CAP) : { text: null, truncated: false };
        ops.push({
          ...base, engine: "alerts", kind: write ? "write" : "browse",
          query: null, target: rule || "alert rules",
          fields: { operation },
          content: c.text, contentTruncated: c.truncated,
        });
      } else if (GRAFANA_BROWSE_RE.test(parsed.action)) {
        const what = str(input.labelName) || str(input.regex) || str(input.query) || str(input.metric) || null;
        ops.push({
          ...base, engine: /loki/.test(parsed.action) ? "loki" : /prometheus|metric/.test(parsed.action) ? "prometheus" : "grafana",
          kind: "browse",
          query: what, target: parsed.action.replace(/_/g, " "),
          fields: {},
          // Browses are navigation, not observation — drop the payload.
          result: { text: null, truncated: false },
          view: null,
        });
      }
    }
  }
  return ops;
}


// ── Grafana response view models ─────────────────────────────────────────────
// The raw responses are single-line JSON — legible to a model, hostile to a
// person. Parse them into typed view models the panel can render richly:
// series (instant values / range points), log entries, or the no-data hint.
// Pure parsing here; drawing stays component-side on the house primitives.

// Labels that identify infrastructure, not the series' meaning — dropped from
// chips unless they're all that distinguishes two series.
const NOISE_LABELS = new Set([
  "__name__", "container_image", "instance", "job", "pod", "endpoint",
  "prometheus", "prometheus_replica", "cluster", "container", "namespace",
]);

function numOf(v) {
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

// Log timestamps arrive as second-floats, ms, or ns strings — normalize to ms.
function logTsMs(v) {
  const n = numOf(v);
  if (n === null) return null;
  if (n > 1e17) return n / 1e6; // ns
  if (n > 1e14) return n / 1e3; // µs
  if (n > 1e12) return n;       // ms
  return n * 1000;              // s
}

// The label keys worth showing: those that VARY across series (they're what
// distinguishes the lines); for a single series, its non-noise labels.
export function varyingLabelKeys(seriesLabels) {
  const all = new Map();
  for (const labels of seriesLabels) {
    for (const [k, v] of Object.entries(labels || {})) {
      const set = all.get(k) || new Set();
      set.add(v);
      all.set(k, set);
    }
  }
  if (seriesLabels.length <= 1) {
    return [...all.keys()].filter((k) => !NOISE_LABELS.has(k)).slice(0, 4);
  }
  const varying = [...all.entries()].filter(([, set]) => set.size > 1).map(([k]) => k);
  const informative = varying.filter((k) => !NOISE_LABELS.has(k));
  return (informative.length ? informative : varying).slice(0, 4);
}

const RESULT_SERIES_CAP = 8;
const RESULT_LOG_CAP = 12;

export function parseGrafanaResult(text) {
  let d = null;
  try { d = JSON.parse(text); } catch { return null; }
  if (!d || typeof d !== "object") return null;

  // Alert-rule listings arrive as a TOP-LEVEL array of rules — state is the
  // headline (a "firing" in the observed payload is the story).
  if (Array.isArray(d)) {
    if (!d.length || !d.every((e) => e && typeof e === "object")) return null;
    if (!d.some((e) => str(e.state) && (str(e.title) || str(e.uid)))) return null;
    return {
      type: "rules",
      total: d.length,
      rules: d.slice(0, RESULT_LOG_CAP).map((e) => ({
        title: str(e.title) || str(e.uid) || "(unnamed rule)",
        state: str(e.state), health: str(e.health),
        group: str(e.rule_group),
      })),
    };
  }

  const data = Array.isArray(d.data) ? d.data : null;
  if (!data) return null;

  if (!data.length) {
    const summary = str(d.hints?.summary);
    return summary ? { type: "hint", summary } : { type: "hint", summary: "The query returned no data." };
  }

  // Log entries: [{ timestamp, line, labels? }] — mixed arrays keep just the
  // line-bearing entries.
  const lineEntries = data.filter((e) => e && typeof e === "object" && "line" in e);
  if (lineEntries.length) {
    const entries = lineEntries.slice(0, RESULT_LOG_CAP).map((e) => {
      let level = null;
      let message = null;
      const line = String(e.line ?? "");
      try {
        const inner = JSON.parse(line);
        level = str(inner.level) || str(inner.severity) || null;
        message = str(inner.message) || str(inner.msg) || str(inner.fields?.message) || null;
      } catch { /* plain-text line */ }
      return { ts: logTsMs(String(e.timestamp ?? "").replace(/"/g, "")), level, message: message || line.slice(0, 400) };
    });
    return { type: "logs", entries, total: lineEntries.length };
  }

  // Series: prometheus instant ({metric, value:[ts,v]}), prometheus range
  // ({metric, values:[[ts,v],…]}), loki metrics ({values:[{timestamp,value}]}).
  const series = [];
  for (const e of data.slice(0, RESULT_SERIES_CAP)) {
    if (!e || typeof e !== "object") continue;
    const labels = e.metric && typeof e.metric === "object" ? e.metric : {};
    let points = null;
    if (Array.isArray(e.values)) {
      points = e.values
        .map((p) => (Array.isArray(p) ? numOf(p[1]) : numOf(p?.value)))
        .filter((n) => n !== null);
    } else if (Array.isArray(e.value)) {
      const n = numOf(e.value[1]);
      points = n === null ? [] : [n];
    } else if (numOf(e.value) !== null) {
      // Loki instant metrics: { timestamp, value: 34, labels: {} }.
      points = [numOf(e.value)];
    }
    if (!points || !points.length) continue;
    if (points.length > 500) points = points.slice(-500);
    series.push({
      labels: Object.keys(labels).length ? labels : (e.labels && typeof e.labels === "object" ? e.labels : {}),
      name: str(labels.__name__) || null,
      points,
      last: points[points.length - 1],
      min: Math.min(...points),
      max: Math.max(...points),
    });
  }
  if (!series.length) return null;
  return {
    type: "series",
    instant: series.every((sr) => sr.points.length === 1),
    series,
    total: data.length,
    varying: varyingLabelKeys(series.map((sr) => sr.labels)),
  };
}

// ── Data queries ─────────────────────────────────────────────────────────────

// A compact, stable rendering of an analytics_query request — the object the
// agent authored. Key order is normalized so identical queries dedup.
// The entity a query is ABOUT, from either arg shape: the MCP tool's flat
// `entity`, or the graph spec's `root.entity` enum. The enum prefix is noise
// in a lane label — `ENTITY_TYPE_ILO_CLUSTER` reads as `ilo_cluster`.
export function analyticsEntityOf(q) {
  const raw = str(q?.entity) || str(q?.root?.entity);
  if (!raw) return null;
  return raw.startsWith("ENTITY_TYPE_") ? raw.slice("ENTITY_TYPE_".length).toLowerCase() : raw;
}

export function analyticsQueryText(input) {
  const pick = {};
  for (const k of ["entity", "alias", "select", "metrics", "filters", "joins", "order_by", "limit", "offset", "sample", "time_range_days"]) {
    if (input?.[k] !== undefined) pick[k] = input[k];
  }
  return JSON.stringify(pick, null, 1);
}

// {columns, rows, cost} → a structured TABLE for the kernel table renderer
// (which brings its own CSV download), plus cost chips.
export function analyticsTableOf(text) {
  let d = null;
  try { d = JSON.parse(text); } catch { return null; }
  if (!d || !Array.isArray(d.columns)) return null;
  // Analytics rows come as {values: [...]} envelopes — unpack them, or every
  // row renders as ONE cell holding the stringified envelope while the header
  // still shows the real columns.
  const rows = (Array.isArray(d.rows) ? d.rows : [])
    .slice(0, TABLE_ROW_CAP)
    .map((row) => (Array.isArray(row) ? row : Array.isArray(row?.values) ? row.values : [row]));
  const fields = { rows: String(Array.isArray(d.rows) ? d.rows.length : 0) };
  if (d.cost && typeof d.cost === "object") {
    if (d.cost.elapsed_ms !== undefined) fields.elapsed = `${d.cost.elapsed_ms}ms`;
    if (d.cost.actual_rows_scanned !== undefined) fields.scanned = String(d.cost.actual_rows_scanned);
  }
  if (d.truncated) fields.truncated = "true";
  return { columns: d.columns.map(String), rows, total: Array.isArray(d.rows) ? d.rows.length : 0, fields };
}

// A conservative TSV/PSV reader for shell SQL output (clickhouse TSVWithNames,
// psql pipes): every line must split into the same ≥2 columns, first line is
// the header. Anything irregular stays raw — the toggle always has the text.
export function tsvTableOf(text) {
  const lines = String(text || "").trim().split("\n");
  if (lines.length < 2) return null;
  const sep = lines[0].includes("\t") ? "\t" : lines[0].includes("|") ? "|" : null;
  if (!sep) return null;
  const header = lines[0].split(sep).map((c) => c.trim());
  if (header.length < 2 || header.some((c) => !c)) return null;
  const rows = [];
  for (const line of lines.slice(1, 1 + TABLE_ROW_CAP)) {
    const cells = line.split(sep).map((c) => c.trim());
    if (cells.length !== header.length) return null;
    rows.push(cells);
  }
  if (!rows.length) return null;
  return { columns: header, rows, total: lines.length - 1, fields: { rows: String(lines.length - 1) } };
}

const SQL_WRITE_RE = /^\s*(insert|alter|create|drop|delete|truncate|optimize|update|grant|revoke)\b/i;

export function sqlTableOf(sql) {
  const m = /\b(?:from|into|table|update|describe|desc)\s+(?:table\s+)?([a-zA-Z_][\w.]*)/i.exec(String(sql || ""));
  const t = m ? m[1] : null;
  // DESCRIBE's own keyword can capture noise like "if"/"exists" — require a
  // plausible identifier.
  return t && !/^(if|not|exists|select)$/i.test(t) ? t : null;
}

// SQL statements driven through the shell. Channels observed on live traffic:
//   clickhouse-client --query 'SQL' / -q 'SQL' / <<'SQL' heredoc
//   psql … -c 'SQL'
//   sqlite3 db 'SQL'
//   curl …clickhouse…/8123… --data-urlencode "query=SQL" (the tailnet
//   read-proxy pattern) — SQL rides the query= parameter.
function shellSqlOf(stmt, bare, bodies, cmdMentionsCH) {
  const grab = (re) => {
    const m = re.exec(stmt);
    if (!m) return null;
    // Group ladder mirrors flagValue: single-quote inner, double-quote inner,
    // then the raw match (bare token).
    let v = m[3] ?? m[2] ?? m[1];
    const hd = /(?:^|\s)HD(\d+)(?:\s|$)/.exec(v || "");
    if (hd) v = bodies[Number(hd[1])] ?? v;
    return str(v);
  };
  if (/(^|[\s;&|(])clickhouse(-client|-local)?\b/.test(bare)) {
    const sql = grab(/(?:--query|--multiquery|-q)[=\s]+("((?:[^"\\]|\\.)*)"|'([^']*)')/)
      || grab(/(?:--query|-q)[=\s]+(\S+)/)
      || (/(?:^|\s)HD(\d+)(?:\s|$)/.test(stmt) ? (bodies[Number(/(?:^|\s)HD(\d+)(?:\s|$)/.exec(stmt)[1])] || null) : null);
    return sql ? { engine: "clickhouse", instance: "clickhouse-client", sql } : null;
  }
  if (/(^|[\s;&|(])psql\b/.test(bare)) {
    const sql = grab(/-c[=\s]+("((?:[^"\\]|\\.)*)"|'([^']*)')/);
    return sql ? { engine: "postgres", instance: "psql", sql } : null;
  }
  if (/(^|[\s;&|(])sqlite3\b/.test(bare)) {
    const sql = grab(/sqlite3\s+\S+\s+("((?:[^"\\]|\\.)*)"|'([^']*)')/);
    return sql ? { engine: "sqlite", instance: "sqlite3", sql } : null;
  }
  // The host often rides an env var set earlier in the SAME command
  // (`CH="https://…-clickhouse…"; curl "$CH" …`) — the command-level marker
  // covers what the statement alone can't see.
  if (/(^|\s)curl\s/.test(bare) && (/clickhouse|:8123/i.test(stmt) || cmdMentionsCH)) {
    // The tailnet read-proxy pattern quotes the whole arg — the quote comes
    // BEFORE query= (`--data-urlencode "query=SELECT …"`).
    let sql = (/"query=((?:[^"\\]|\\.)*)"/.exec(stmt)
      || /'query=([^']*)'/.exec(stmt)
      || /query=([^"'&\s]+)/.exec(stmt))?.[1] || null;
    if (!sql) {
      // Raw-body variant: curl "$CH" --data-binary "WITH … SELECT …".
      const raw = /(?:--data-binary|--data-raw|--data|-d)\s+("((?:[^"\\]|\\.)*)"|'([^']*)')/.exec(stmt);
      const body = raw ? (raw[3] ?? raw[2] ?? "").trim() : "";
      if (/^(select|with|insert|describe|desc|show|alter|create|drop|optimize)\b/i.test(body)) sql = body;
    }
    if (!sql) return null;
    const host = /https?:\/\/([a-z0-9.-]+)/i.exec(stmt)?.[1] || null;
    return { engine: "clickhouse", instance: host ? host.split(".")[0] : "clickhouse", sql: sql.replace(/\s+FORMAT\s+\w+\s*$/i, "") };
  }
  return null;
}

// One prompt's turns → its data-query ops (MCP + shell doors).
export function extractDataQueryOps(turns) {
  const ops = [];
  if (!Array.isArray(turns)) return ops;
  const resultById = new Map();
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type === "tool_result" && b.tool_use_id) resultById.set(b.tool_use_id, b);
    }
  }
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type !== "tool_use") continue;
      const input = b.input && typeof b.input === "object" ? b.input : {};
      const result = b.id ? resultById.get(b.id) : null;
      const ok = !resultErrored(result);
      const resText = ok && result ? contentText(result.content) : "";

      const parsed = parseMcpName(b.name);
      // The analytics door has two forms: MCP-prefixed (captured tenant
      // traces) and BARE (the dashboard's own in-app agents — chat, the
      // Discover scout — call the tools directly). The substrate matcher
      // already treats bare `analytics_query` as data-query; the extractor
      // has to agree or in-app runs render as unclaimed JSON.
      const bare = String(b.name || "");
      if ((parsed && parsed.action === "analytics_query") || bare === "analytics_query") {
        // Two arg shapes for the same door. The MCP tool takes the query
        // FLAT (entity/select/filters); the dashboard's own tool takes the
        // graph Query spec wrapped under `spec` — and its entity lives at
        // spec.root.entity, not spec.entity. Reading the flat shape against
        // a wrapped call yields an empty query and a generic target, which
        // is the whole content the lens exists to show.
        const spec = input.spec && typeof input.spec === "object" ? input.spec : null;
        const qIn = spec || input;
        const q = cap(spec ? JSON.stringify(spec, null, 1) : analyticsQueryText(qIn), QUERY_CAP);
        const table = resText ? analyticsTableOf(resText) : null;
        const res = cap(resText, RESULT_CAP);
        const instance = parsed
          ? (parsed.server.length > 20 ? `${parsed.server.slice(0, 17)}…` : parsed.server)
          : "dashboard";
        ops.push({
          key: b.id || `synth:${hashString(q.text)}`,
          channel: "mcp", engine: "analytics",
          instance,
          kind: "query", query: q.text,
          target: analyticsEntityOf(qIn) || "query",
          fields: { ...(table ? table.fields : {}), ...(qIn.limit ? { limit: String(qIn.limit) } : {}) },
          content: null,
          result: { text: res.text || null, truncated: res.truncated },
          table,
          ok,
        });
        continue;
      }
      // The bare toolset's siblings: the schema catalogue read and the
      // stored-rows window read. Browses, not queries — they carry no spec.
      if (bare === "analytics_describe_schema" || bare === "expand_preview") {
        const isPreview = bare === "expand_preview";
        const q = isPreview ? cap(JSON.stringify(input, null, 1), QUERY_CAP) : { text: null, truncated: false };
        ops.push({
          key: b.id || `synth:${hashString(bare + (q.text || ""))}`,
          channel: "mcp", engine: "analytics", instance: "dashboard",
          kind: "browse",
          query: q.text === "{}" ? null : q.text,
          target: isPreview ? "stored rows" : "schema",
          fields: {},
          content: null,
          result: { text: null, truncated: false },
          ok,
        });
        continue;
      }
      if (parsed && /emissary/i.test(parsed.server) && /^(query_|list_|view_|collect_)/.test(parsed.action)) {
        const isQuery = /^query_/.test(parsed.action);
        const q = cap(JSON.stringify(input, null, 1), QUERY_CAP);
        const res = cap(isQuery ? resText : "", RESULT_CAP);
        ops.push({
          key: b.id || `synth:${hashString(parsed.action + q.text)}`,
          channel: "mcp", engine: "emissary", instance: parsed.server,
          kind: isQuery ? "query" : "browse",
          query: q.text === "{}" ? null : q.text,
          target: parsed.action.replace(/^(query_|list_|view_|collect_)/, "") || parsed.action,
          fields: {},
          content: null,
          result: { text: res.text || null, truncated: res.truncated },
          ok,
        });
        continue;
      }

      // Shell door — same statement discipline as the Git tab.
      const name = String(b.name || "");
      const last = name.split("__").pop();
      if (!/^(bash|bash_tool|powershell|shell|run_terminal_cmd|execute_command)$/i.test(last)) continue;
      const cmd = str(input.command) || str(input.cmd) || str(input.script);
      if (!cmd) continue;
      const { text, bodies } = stripHeredocs(cmd);
      const cmdMentionsCH = /clickhouse|:8123/i.test(cmd);
      const found = [];
      for (const stmt of splitStatements(text)) {
        const hit = shellSqlOf(stmt, unquoted(stmt), bodies, cmdMentionsCH);
        if (hit) found.push(hit);
        if (found.length >= 4) break;
      }
      found.forEach((hit, i) => {
        const q = cap(hit.sql.trim(), QUERY_CAP);
        // The tool_result IS keyed to this call — what a multi-statement
        // command loses is only which SLICE of one stdout belongs to which
        // statement. Dropping the result entirely to avoid that ambiguity
        // discarded the run's only evidence and read as a broken lens, so
        // the shared output attaches to each statement flagged `shared`,
        // and the reader is told what they are looking at. The TABLE still
        // requires a single owner: parsing one blob as one statement's rows
        // would be the wrong claim, not just an imprecise one.
        const single = found.length === 1;
        const res = cap(resText, RESULT_CAP);
        ops.push({
          key: b.id ? `${b.id}#${i}` : `synth:${hashString(cmd)}#${i}`,
          channel: "shell", engine: hit.engine, instance: hit.instance,
          kind: SQL_WRITE_RE.test(q.text) ? "write" : "query",
          query: q.text,
          target: sqlTableOf(q.text) || hit.engine,
          fields: {},
          content: null,
          result: {
            text: res.text || null,
            truncated: res.truncated,
            ...(single ? {} : { shared: found.length }),
          },
          table: single && resText ? tsvTableOf(resText) : null,
          ok,
        });
      });
    }
  }
  return ops;
}

// ── Window collection + aggregation (shared by both tabs) ────────────────────

function tsOf(v) {
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return v;
  const p = Date.parse(v);
  return Number.isNaN(p) ? null : p;
}

export function collectWindowQueryOps(promptEvents) {
  const sorted = [...(promptEvents || [])].sort((a, b) => (tsOf(a.ts) ?? 0) - (tsOf(b.ts) ?? 0));
  const seen = new Set();
  const flat = [];
  for (const p of sorted) {
    for (const op of p.ops || []) {
      const dedupKey = `${p.sessionId || ""}|${op.key}`;
      if (seen.has(dedupKey)) continue;
      seen.add(dedupKey);
      flat.push({
        ...op,
        promptId: p.promptId,
        sessionId: p.sessionId || "",
        endpointId: p.endpointId || "",
        endpointName: p.endpointName || "",
        ts: p.ts ?? null,
      });
    }
  }
  return flat;
}

function shortId(id) {
  return id ? `${String(id).slice(0, 8)}…` : "unknown";
}

// The window's query activity — engine/instance-, target-, and actor-centric:
//   groups:  [{ key, engine, instance, targets: [target…], queries, writes }]
//   targets: [{ key, engine, instance, target, queries: [{ text, runs:[op…],
//              lastResult }], reads, writes, errored, sessions, actors }]
//   actors:  [{ id, name, queries, writes, sessions }]
//   totals:  { targets, queries, runs, reads, writes, browses, errored,
//              engines: {...}, actors }
export function buildQueryActivity(flatOps) {
  const targets = new Map();
  const actors = new Map();
  const engines = {};
  let reads = 0;
  let writes = 0;
  let browses = 0;
  let errored = 0;
  let runs = 0;

  for (const op of flatOps || []) {
    let actor = actors.get(op.endpointId);
    if (!actor) {
      actor = { id: op.endpointId, name: op.endpointName || shortId(op.endpointId), queries: 0, writes: 0, sessions: new Set() };
      actors.set(op.endpointId, actor);
    }
    actor.sessions.add(op.sessionId);
    engines[op.engine] = (engines[op.engine] || 0) + 1;
    runs += 1;
    if (!op.ok) errored += 1;
    else if (op.kind === "write") { writes += 1; actor.writes += 1; }
    else if (op.kind === "browse") browses += 1;
    else { reads += 1; actor.queries += 1; }

    const tKey = `${op.engine}|${op.instance || ""}|${op.target}`;
    let t = targets.get(tKey);
    if (!t) {
      t = {
        key: tKey, engine: op.engine, instance: op.instance || null, target: op.target,
        queries: new Map(), reads: 0, writes: 0, errored: 0, browses: 0,
        sessions: new Set(), actors: new Set(),
      };
      targets.set(tKey, t);
    }
    t.sessions.add(op.sessionId);
    t.actors.add(op.endpointId);
    if (!op.ok) t.errored += 1;
    else if (op.kind === "write") t.writes += 1;
    else if (op.kind === "browse") t.browses += 1;
    else t.reads += 1;

    const qText = op.query || op.content || "(no query text)";
    let q = t.queries.get(qText);
    if (!q) {
      q = { text: qText, runs: [], lastResult: null };
      t.queries.set(qText, q);
    }
    q.runs.push({
      promptId: op.promptId, sessionId: op.sessionId, endpointId: op.endpointId,
      endpointName: op.endpointName, ts: op.ts, channel: op.channel, kind: op.kind,
      ok: op.ok, fields: op.fields || {},
      result: op.result || { text: null, truncated: false },
      view: op.view || null,
      table: op.table || null,
      content: op.content || null,
    });
    if (op.ok && op.result?.text) q.lastResult = { text: op.result.text, truncated: op.result.truncated, ts: op.ts, promptId: op.promptId };
  }

  const targetList = [...targets.values()].map((t) => ({
    ...t,
    queries: [...t.queries.values()].map((q) => ({
      ...q,
      runs: [...q.runs].sort((a, b) => (tsOf(a.ts) ?? 0) - (tsOf(b.ts) ?? 0)),
    })).sort((a, b) => b.runs.length - a.runs.length),
    sessions: t.sessions.size,
    actors: t.actors.size,
  }));
  // Writes first (rarer, higher-signal), then interrogation volume.
  targetList.sort((a, b) => (b.writes - a.writes) || (b.reads + b.browses) - (a.reads + a.browses) || String(a.target).localeCompare(String(b.target)));

  const groups = new Map();
  for (const t of targetList) {
    const gKey = `${t.engine}|${t.instance || ""}`;
    let g = groups.get(gKey);
    if (!g) {
      g = { key: gKey, engine: t.engine, instance: t.instance, targets: [], reads: 0, writes: 0 };
      groups.set(gKey, g);
    }
    g.targets.push(t);
    g.reads += t.reads + t.browses;
    g.writes += t.writes;
  }
  const groupList = [...groups.values()].sort((a, b) => (b.writes - a.writes) || (b.reads - a.reads));

  const actorList = [...actors.values()]
    .filter((a) => a.queries + a.writes > 0)
    .map((a) => ({ id: a.id, name: a.name, queries: a.queries, writes: a.writes, sessions: a.sessions.size }));
  actorList.sort((a, b) => (b.writes - a.writes) || (b.queries - a.queries) || a.name.localeCompare(b.name));

  return {
    groups: groupList,
    targets: targetList,
    actors: actorList,
    totals: {
      targets: targetList.length,
      queries: targetList.reduce((s, t) => s + t.queries.length, 0),
      runs, reads, writes, browses, errored,
      engines,
      actors: actorList.length,
    },
  };
}

// A response as a read observation for the annotated renderer — numbered
// lines, truncation = the document's length can't be pinned.
export function resultObsDoc(run) {
  const text = run?.result?.text;
  if (!text) return null;
  const lines = text.split("\n");
  return {
    kind: "read", ok: run.ok !== false,
    obs: lines.map((t, i) => [i + 1, t]),
    obsFull: !run.result.truncated,
    readLines: lines.length,
    promptId: run.promptId, sessionId: run.sessionId, ts: run.ts,
  };
}
